import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  Plus, Search, Loader2, ChevronDown, Tags, Eye, EyeOff, X,
  BookOpen, FileSpreadsheet, Trash2, Pencil, Archive, Package, Upload,
} from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { ListSkeleton } from "../components/Skeletons";
import ImportPreviewDialog from "../components/ImportPreviewDialog";
import { Layers, CheckSquare, Square } from "lucide-react";
import { ScormMultiImportButton } from "../components/lms/ScormImportButton";

const inputCls = "bg-[#0b0811] border border-white/10 text-white placeholder:text-slate-500 focus:border-[#f7c935]";

const TABS = [
  { k: "programmes", l: "Programmes", icon: BookOpen },
  { k: "tableau", l: "Tableau de programmes", icon: FileSpreadsheet },
  { k: "categories", l: "Categories", icon: Tags },
  { k: "export_edof", l: "Export EDOF", icon: FileSpreadsheet },
];

export default function BibliothequePage() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("programmes");

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="bibliotheque-page">
      <PageHeader
        icon={<BookOpen />}
        title="Programmes de formations"
        description="Créez, importez ou gérez vos programmes. Importez à partir d'un DOCX ou d'un PDF existant pour démarrer en 1 clic."
        breadcrumb={[{ label: "Formation" }, { label: "Programmes" }]}
        testId="bibliotheque-header"
      />

      <div className="flex gap-1 mb-6 border-b border-white/10">
        {TABS.map(t => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`biblio-tab-${t.k}`}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold transition-colors ${tab === t.k ? "text-[#f7c935] border-b-2 border-[#f7c935]" : "text-slate-400 hover:text-white"}`}
          >
            <t.icon className="w-4 h-4" /> {t.l}
          </button>
        ))}
      </div>

      {tab === "programmes" && <ProgrammesTab api={api} navigate={navigate} />}
      {tab === "tableau" && <TableauTab api={api} />}
      {tab === "categories" && <CategoriesTab api={api} />}
      {tab === "export_edof" && <ExportEdofTab />}
    </div>
  );
}

// ─── Programmes Tab (list with filters) ────────────────────
function ProgrammesTab({ api, navigate }) {
  const [programs, setPrograms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [bloc, setBloc] = useState("");
  const [visibility, setVisibility] = useState("");
  const [archived, setArchived] = useState(false);
  const [sort, setSort] = useState("recent");
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const activeFilters = [categoryId && "categorie", bloc && "bloc", visibility && "visibilite"].filter(Boolean);
  const importInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [previewDraft, setPreviewDraft] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (categoryId) params.set("category_id", categoryId);
      if (bloc) params.set("bloc", bloc);
      if (visibility) params.set("visibility", visibility);
      if (archived) params.set("archived", "true");
      if (sort) params.set("sort", sort);
      const [p, c] = await Promise.all([
        api.get(`/lms/programs?${params.toString()}`),
        api.get("/lms/categories"),
      ]);
      setPrograms(p.data || []);
      setCategories(c.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  // eslint-disable-next-line
  }, [search, categoryId, bloc, visibility, archived, sort]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [search, categoryId, bloc, visibility, archived, sort]);

  const clearAll = () => { setSearch(""); setCategoryId(""); setBloc(""); setVisibility(""); setSort("recent"); };

  const create = async (data) => {
    try {
      const r = await api.post("/lms/programs", data);
      toast.success("Programme cree");
      setShowCreate(false);
      navigate(`/admin/formation/bibliotheque/${r.data.program_id}/edit`);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const quickCreate = () => setShowCreate(true);

  const importFile = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/lms/programs/import-preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setShowCreate(false);
      setPreviewDraft(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
    setImporting(false);
  };

  const confirmImport = async (draft) => {
    setConfirming(true);
    try {
      const r = await api.post("/lms/programs/import-confirm", draft);
      const pid = r.data?.program_id;
      if (!pid) throw new Error("Import sans program_id");
      const nMod = r.data?.modules_created || 0;
      toast.success(`Programme cree (${r.data?.sections_filled || 0} sections${nMod ? `, ${nMod} module${nMod > 1 ? "s" : ""}` : ""})`);
      setPreviewDraft(null);
      navigate(`/admin/formation/bibliotheque/${pid}/edit`);
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
    setConfirming(false);
  };

  const handleImport = async (e) => {
    await importFile(e.target.files?.[0]);
    e.target.value = "";
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={quickCreate} className="bg-[#f7c935] text-[#14101c] font-bold hover:bg-[#ffd948]" data-testid="create-program-btn">
          <Plus className="w-4 h-4 mr-2" /> Creer un programme
        </Button>
        <input ref={importInputRef} type="file" accept=".docx,.pdf,.html,.htm" onChange={handleImport} className="hidden" data-testid="import-program-file" />
        <Button
          onClick={() => importInputRef.current?.click()}
          disabled={importing}
          variant="outline"
          className="border-[#f7c935]/30 text-[#f7c935] hover:bg-[#f7c935]/10"
          data-testid="import-program-btn"
        >
          {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
          {importing ? "Analyse IA en cours..." : "Importer depuis un DOCX / PDF"}
        </Button>
        <span className="text-[10px] text-slate-500 ml-1">Detecte titre, objectifs, public, contenu, duree, prix... et cree le programme pret a editer.</span>
      </div>

      {/* Filters row */}
      <div className="flex flex-wrap gap-2 items-center bg-[#0b0811] border border-white/10 rounded-xl p-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input placeholder="Rechercher" value={search} onChange={e => setSearch(e.target.value)} className={`${inputCls} pl-9`} data-testid="biblio-search" />
        </div>
        <select value={sort} onChange={e => setSort(e.target.value)} className={`${inputCls} px-3 py-2 rounded text-sm`} data-testid="biblio-sort">
          <option value="recent">Trier : recent</option>
          <option value="title">Trier : titre</option>
          <option value="variants">Trier : nb variantes</option>
        </select>
        <select value={categoryId} onChange={e => setCategoryId(e.target.value)} className={`${inputCls} px-3 py-2 rounded text-sm`} data-testid="biblio-category">
          <option value="">Categorie</option>
          {categories.map(c => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
        </select>
        <Input placeholder="Bloc de competences" value={bloc} onChange={e => setBloc(e.target.value)} className={`${inputCls} w-48`} data-testid="biblio-bloc" />
        <select value={visibility} onChange={e => setVisibility(e.target.value)} className={`${inputCls} px-3 py-2 rounded text-sm`} data-testid="biblio-visibility">
          <option value="">Visibilite en ligne</option>
          <option value="public">Publie</option>
          <option value="draft">Brouillon</option>
        </select>
        <button onClick={() => setArchived(v => !v)} className={`text-xs px-3 py-1 rounded ${archived ? "bg-[#f7c935] text-[#14101c]" : "bg-white/5 text-slate-400 hover:text-white"}`} data-testid="biblio-archived-toggle">
          {archived ? <Archive className="w-3 h-3 inline mr-1" /> : null} Archivees
        </button>
        {(search || categoryId || bloc || visibility) && (
          <button onClick={clearAll} className="text-xs text-slate-400 hover:text-white flex items-center gap-1"><X className="w-3 h-3" />Effacer tout</button>
        )}
      </div>

      {loading ? <ListSkeleton items={5} testId="biblio-loading-skeleton" /> :
        programs.length === 0 ? (
          <div className="text-center py-20 text-slate-500 border border-dashed border-white/10 rounded-xl">
            <BookOpen className="w-12 h-12 mx-auto text-slate-700 mb-3" />
            Aucun programme. Creez le premier.
          </div>
        ) : (
          <div className="bg-[#14101c] border border-white/10 rounded-xl divide-y divide-white/5">
            {programs.map(p => (
              <Link
                key={p.program_id}
                to={`/admin/formation/bibliotheque/${p.program_id}`}
                data-testid={`biblio-program-${p.program_id}`}
                className="w-full text-left px-5 py-4 flex items-center gap-4 hover:bg-white/5 transition-colors"
              >
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: p.color || "#f59e0b" }} />
                <span className="font-medium text-white flex-1 truncate">{p.title}</span>
                <span className="text-xs text-slate-500 font-mono">{p.code_interne}</span>
                {p.variants_count > 0 && <span className="text-xs text-slate-400">{p.variants_count} variante{p.variants_count > 1 ? "s" : ""}</span>}
                {p.publie_catalogue && <Eye className="w-4 h-4 text-green-400" title="Publie sur le catalogue" />}
                {p.eligible_cpf && <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300">CPF</span>}
              </Link>
            ))}
          </div>
        )}

      {showCreate && <CreateProgramDialog onClose={() => setShowCreate(false)} onCreate={create} onImport={importFile} importing={importing} categories={categories} api={api} />}
      {previewDraft && <ImportPreviewDialog draft={previewDraft} onClose={() => setPreviewDraft(null)} onConfirm={confirmImport} confirming={confirming} />}
    </div>
  );
}

// ─── Create Program Quick Dialog ───────────────────────────
function CreateProgramDialog({ onClose, onCreate, onImport, importing, categories, api }) {
  const [title, setTitle] = useState("");
  const [sousTitre, setSousTitre] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [modality, setModality] = useState("presentiel");
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);
  // Iter 319 — leçons rattachées dès la création
  const [lessons, setLessons] = useState([]);
  const [selectedLessons, setSelectedLessons] = useState([]);
  useEffect(() => { api?.get("/lms/lessons").then((r) => setLessons(r.data || [])).catch(() => {}); /* eslint-disable-next-line */ }, []);
  const toggleLesson = (id) => setSelectedLessons((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const submit = async () => {
    if (!title.trim()) { toast.error("Titre requis"); return; }
    setLoading(true);
    await onCreate({ title: title.trim(), sous_titre: sousTitre, category_id: categoryId, modality, linked_lesson_ids: selectedLessons });
    setLoading(false);
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg bg-[#0b0811] border border-white/10 text-white">
        <DialogHeader><DialogTitle>Nouveau programme</DialogTitle></DialogHeader>

        {/* Import OCR depuis un document (Iter 259) */}
        <div className="border border-dashed border-[#f7c935]/30 rounded-xl p-4 text-center bg-[#f7c935]/[0.03]" data-testid="new-prog-import-zone">
          <input
            ref={fileRef} type="file" accept=".docx,.pdf,.html,.htm" className="hidden"
            onChange={async (e) => { await onImport(e.target.files?.[0]); e.target.value = ""; }}
            data-testid="new-prog-import-file"
          />
          <Button
            onClick={() => fileRef.current?.click()}
            disabled={importing}
            variant="outline"
            className="border-[#f7c935]/40 text-[#f7c935] hover:bg-[#f7c935]/10"
            data-testid="new-prog-import-btn"
          >
            {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {importing ? "Analyse du document en cours..." : "Importer depuis un DOCX / PDF / HTML"}
          </Button>
          <p className="text-[11px] text-slate-500 mt-2">Cree automatiquement le programme complet : titre, sections, objectifs, duree, prix et modules detectes.</p>
        </div>

        <div className="flex items-center gap-3 text-[11px] text-slate-500 uppercase tracking-wider">
          <span className="flex-1 h-px bg-white/10" /> ou creez manuellement <span className="flex-1 h-px bg-white/10" />
        </div>

        <div className="space-y-3">
          <Input placeholder="Titre du programme *" value={title} onChange={e => setTitle(e.target.value)} className={inputCls} data-testid="new-prog-title" autoFocus />
          <Input placeholder="Sous-titre (ex: Certification RS7220)" value={sousTitre} onChange={e => setSousTitre(e.target.value)} className={inputCls} />
          <div className="grid grid-cols-2 gap-2">
            <select value={categoryId} onChange={e => setCategoryId(e.target.value)} className={`${inputCls} px-3 py-2 rounded`}>
              <option value="">— Categorie —</option>
              {categories.map(c => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
            </select>
            <select value={modality} onChange={e => setModality(e.target.value)} className={`${inputCls} px-3 py-2 rounded`}>
              <option value="presentiel">Presentiel</option>
              <option value="distanciel">Distanciel</option>
              <option value="mixte">Mixte</option>
              <option value="elearning">E-learning</option>
            </select>
          </div>
          {/* Iter 324 — Modules de cours (séquences SCORM) rattachés dès la création */}
          <div className="bg-[#14101c] border border-white/10 rounded-xl p-3" data-testid="new-prog-lessons">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold flex items-center gap-1"><Layers className="w-3 h-3 text-sky-400" /> Modules de cours du programme <span className="text-slate-600">({selectedLessons.length})</span></div>
              <ScormMultiImportButton api={api} className="h-7 text-[11px]" label="Importer des SCORM → module" testId="new-prog-scorm" onDone={(d) => { setLessons((ls) => [d.lesson, ...ls.filter((x) => x.lesson_id !== d.lesson.lesson_id)]); setSelectedLessons((s) => (s.includes(d.lesson.lesson_id) ? s : [...s, d.lesson.lesson_id])); }} />
            </div>
            {lessons.length === 0 ? (
              <p className="text-[11px] text-slate-500 italic">Aucun module de cours disponible — importez plusieurs packages SCORM d'un coup pour en créer un.</p>
            ) : (
              <div className="max-h-40 overflow-y-auto space-y-1">
                {lessons.map((l) => {
                  const on = selectedLessons.includes(l.lesson_id);
                  return (
                    <button key={l.lesson_id} type="button" onClick={() => toggleLesson(l.lesson_id)} className={"w-full flex items-center gap-2 text-left px-2 py-1.5 rounded text-xs " + (on ? "bg-sky-500/10 text-white" : "text-slate-300 hover:bg-white/5")} data-testid={"new-prog-lesson-" + l.lesson_id}>
                      {on ? <CheckSquare className="w-3.5 h-3.5 text-sky-400 shrink-0" /> : <Square className="w-3.5 h-3.5 text-slate-600 shrink-0" />}
                      <span className="flex-1 truncate">{l.title}</span>
                      <span className="text-[10px] text-slate-500">{(l.module_ids || []).length} séq. SCORM</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <p className="text-xs text-slate-500">Vous pourrez ensuite completer les 11 sections (Modalites, Description, Objectifs, etc.) dans l'editeur.</p>
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={loading || !title.trim()} className="bg-[#f7c935] text-[#14101c] font-bold" data-testid="save-new-prog-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />} Creer et ouvrir
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Tableau (flat table view) ─────────────────────────────
function TableauTab({ api }) {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/lms/programs").then(r => { setPrograms(r.data || []); setLoading(false); }).catch(() => setLoading(false));
  // eslint-disable-next-line
  }, []);

  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-[#f7c935] mx-auto my-10" />;
  if (programs.length === 0) return <div className="text-center py-10 text-slate-500">Aucun programme</div>;

  return (
    <div className="overflow-x-auto bg-[#14101c] border border-white/10 rounded-xl">
      <table className="w-full text-sm">
        <thead className="bg-white/5 text-slate-400 text-xs uppercase">
          <tr>
            <th className="px-4 py-3 text-left">Code</th>
            <th className="px-4 py-3 text-left">Titre</th>
            <th className="px-4 py-3 text-left">Modalite</th>
            <th className="px-4 py-3 text-left">Duree</th>
            <th className="px-4 py-3 text-left">Prix HT</th>
            <th className="px-4 py-3 text-left">CPF</th>
            <th className="px-4 py-3 text-left">Version</th>
            <th className="px-4 py-3 text-left">Variantes</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {programs.map(p => (
            <tr key={p.program_id} className="hover:bg-white/5">
              <td className="px-4 py-3 font-mono text-xs text-slate-400">{p.code_interne}</td>
              <td className="px-4 py-3 font-semibold">
                <span className="inline-block w-2 h-2 rounded-full mr-2" style={{ background: p.color }} />
                {p.title}
              </td>
              <td className="px-4 py-3">{p.modality}</td>
              <td className="px-4 py-3">{p.duration_hours}h / {p.duration_days}j</td>
              <td className="px-4 py-3">{p.prix_entreprise || p.price_ht || 0} €</td>
              <td className="px-4 py-3">{p.eligible_cpf ? "Oui" : "—"}</td>
              <td className="px-4 py-3 text-xs">{p.version || "—"}</td>
              <td className="px-4 py-3">{p.variants_count || 0}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Categories Tab ────────────────────────────────────────
function CategoriesTab({ api }) {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/lms/categories");
      setCats(r.data || []);
    } finally { setLoading(false); }
  // eslint-disable-next-line
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="space-y-4">
      <div><Button onClick={() => setShowCreate(true)} className="bg-[#f7c935] text-[#14101c] font-bold" data-testid="create-cat-btn"><Plus className="w-4 h-4 mr-2" />Nouvelle categorie</Button></div>
      {loading ? <Loader2 className="w-6 h-6 animate-spin text-[#f7c935] mx-auto my-10" /> :
        cats.length === 0 ? <div className="text-center py-10 text-slate-500 border border-dashed border-white/10 rounded-xl">Aucune categorie</div> :
          <div className="grid md:grid-cols-2 gap-2">
            {cats.map(c => (
              <div key={c.category_id} className="bg-[#14101c] border border-white/10 rounded p-3 flex items-center gap-3" data-testid={`cat-${c.category_id}`}>
                <span className="w-4 h-4 rounded" style={{ background: c.color }} />
                <div className="flex-1">
                  <div className="font-semibold">{c.name}</div>
                  {c.description && <div className="text-xs text-slate-500">{c.description}</div>}
                </div>
                <Button size="sm" variant="ghost" onClick={() => setEditing(c)} className="text-slate-300"><Pencil className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" onClick={async () => { if (window.confirm("Supprimer ?")) { await api.delete(`/lms/categories/${c.category_id}`); load(); } }} className="text-red-400"><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>}
      {(showCreate || editing) && <CategoryDialog cat={editing} api={api} onClose={() => { setShowCreate(false); setEditing(null); load(); }} />}
    </div>
  );
}

function CategoryDialog({ cat, api, onClose }) {
  const [name, setName] = useState(cat?.name || "");
  const [color, setColor] = useState(cat?.color || "#f7c935");
  const [desc, setDesc] = useState(cat?.description || "");

  const save = async () => {
    try {
      if (cat) await api.put(`/lms/categories/${cat.category_id}`, { name, color, description: desc });
      else await api.post("/lms/categories", { name, color, description: desc });
      toast.success("OK");
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md bg-[#0b0811] border border-white/10 text-white">
        <DialogHeader><DialogTitle>{cat ? "Modifier" : "Nouvelle"} categorie</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Nom" value={name} onChange={e => setName(e.target.value)} className={inputCls} />
          <div className="flex items-center gap-2">
            <input type="color" value={color} onChange={e => setColor(e.target.value)} className="w-12 h-10 rounded cursor-pointer" />
            <Input value={color} onChange={e => setColor(e.target.value)} className={inputCls} />
          </div>
          <Input placeholder="Description (optionnel)" value={desc} onChange={e => setDesc(e.target.value)} className={inputCls} />
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button onClick={save} disabled={!name.trim()} className="bg-[#f7c935] text-[#14101c] font-bold">Enregistrer</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Export EDOF ───────────────────────────────────────────
function ExportEdofTab() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [downloading, setDownloading] = useState(false);

  const download = async () => {
    setDownloading(true);
    try {
      const r = await api.get("/lms/export/edof", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/xml" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `edof-export-${new Date().toISOString().slice(0, 10)}.xml`;
      document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
      toast.success("Export XML telecharge");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setDownloading(false); }
  };

  return (
    <div className="bg-[#14101c] border border-white/10 rounded-xl p-8 text-center max-w-xl mx-auto" data-testid="edof-tab">
      <FileSpreadsheet className="w-12 h-12 mx-auto text-[#f7c935] mb-3" />
      <h3 className="font-bold text-white mb-1">Export EDOF (Mon Compte Formation)</h3>
      <p className="text-sm text-slate-400 max-w-md mx-auto mb-4">
        Generez un fichier XML conforme au registre EDOF contenant tous vos programmes eligibles CPF
        et leurs sessions actives. Format compatible import Caisse des Depots.
      </p>
      <Button onClick={download} disabled={downloading} className="bg-[#f7c935] text-[#14101c] font-bold hover:bg-[#ffd948]" data-testid="export-edof-btn">
        {downloading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
        Telecharger l'export XML EDOF
      </Button>
      <p className="text-[10px] text-slate-500 mt-3">Programmes inclus : eligible_cpf=true ET non archives</p>
    </div>
  );
}
